import React, { useEffect, useState } from 'react';
import Icon from './icons';
import { STEPS, OFFICES, officeOf, officeInfo, LOCKED_OFFICE, projectPlan } from './types';
import type { Project, Office } from './types';
import { OfficePill } from './stepcommon';
import { BOND_AXIS, TermFilter, TermPills, WITHDRAW_AXIS, passes, useTermFilter } from './terms';
import { fpList } from './coterms';
import { repAlert } from './report';

interface Props {
  projects: Project[];
  onOpen: (id: string) => void;
  onNew: () => void;
  /** 올댓벤처스 어드민 안(LOCKED_OFFICE) — 왼쪽 메뉴가 없어 LP 후보로 가는 단추를 머리에 (2026-10-17) */
  onLp?: () => void;
  lpWait?: number;
  onDelete: (id: string) => void;
  /** 그룹 지정 · 빼기(null) · 이름 바꾸기 — 여러 건 한 번에 (2026-10-08) */
  onGroup: (ids: string[], group: string | null) => Promise<boolean>;
  /** 별표(신경 써야 하는 조합) 켜기 · 끄기 (2026-10-19) */
  onStar?: (id: string, on: boolean) => void;
  /** 조합별 매칭된 LP 후보 회사 이름 (LP 후보 메모 · 2026-10-08) */
  lpByProject?: Record<string, string[]>;
}

// 한글 IME 조합 중 마지막 글자 끊김 방지 입력 (조합 끝나면 commit)
function IMEInput({ value, onChange, ...rest }: { value: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const composing = React.useRef(false);
  React.useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  return <input value={local}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={e => { composing.current = false; onChange(e.currentTarget.value); }}
    onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
    {...rest} />;
}

type OfficeFilter = 'all' | Office;
const FOLD_KEY = 'venture.groupFold';   // 접어 둔 그룹 기억 (브라우저별 편의)
const NOGROUP = '\u0000미분류';          // 그룹 없는 줄의 내부 열쇠 (사용자 그룹 이름과 겹치지 않게)
const readFold = (): Record<string, boolean> => { try { const v = JSON.parse(localStorage.getItem(FOLD_KEY) || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; } };
const groupOf = (p: Project): string => (typeof p.group === 'string' ? p.group.trim() : '');
// 보기 기준 · 정렬 (2026-10-08 지도사 「진행단계별로 솔팅 또는 모아보고 싶다」) — 브라우저별 기억
type GroupBy = 'group' | 'step' | 'none';
type SortKey = '' | 'step' | 'org';
const VIEW_KEY = 'venture.listView';
const readView = (): { by: GroupBy; sort: SortKey; dir: 1 | -1 } => {
  try {
    const v = JSON.parse(localStorage.getItem(VIEW_KEY) || '{}');
    return { by: ['group', 'step', 'none'].includes(v.by) ? v.by : 'group', sort: ['step', 'org'].includes(v.sort) ? v.sort : '', dir: v.dir === -1 ? -1 : 1 };
  } catch { return { by: 'group', sort: '', dir: 1 }; }
};
const stepRank = (p: Project): number => (p.done ? STEPS.length + 1 : p.step);   // 완료는 맨 뒤
const FILTER_KEY = 'venture.officeFilter';   // 보던 사무소 필터 기억 (브라우저별 편의)
const readFilter = (): OfficeFilter => { try { const v = localStorage.getItem(FILTER_KEY); return v === 'roum' || v === 'atv' ? v : 'all'; } catch { return 'all'; } };

export default function VentureList({ projects, onOpen, onNew, onDelete, onGroup, onStar, lpByProject, onLp, lpWait }: Props) {
  const [starOnly, setStarOnly] = useState(false);   // 별표만 보기(2026-10-19)
  const starN = projects.filter((p) => p.star).length;
  const [armed, setArmed] = useState<string | null>(null);
  // 그룹 (2026-10-08 지도사 「그룹핑 하는 기능 · 그룹명도 정하고」) — 체크해서 묶고, 그룹 머리에서 이름 바꾸기 · 해제 · 접기
  const [sel, setSel] = useState<Set<string>>(() => new Set());
  const [fold, setFold] = useState<Record<string, boolean>>(readFold);
  const [moveTo, setMoveTo] = useState('');          // 선택 바 — 옮길 그룹 ('' 고르기 전 · '+' 새 그룹)
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState<{ from: string; to: string } | null>(null);
  const [ungroupArmed, setUngroupArmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 올댓벤처스 어드민 안에서는 「진행단계별」 한 가지 (2026-10-17 지도사 「단계별로만 나오고」)
  const [lv, setLv] = useState(() => (LOCKED_OFFICE ? { ...readView(), by: 'step' as GroupBy } : readView()));
  const putLv = (n: Partial<{ by: GroupBy; sort: SortKey; dir: 1 | -1 }>) => setLv((cur) => { const v = { ...cur, ...n }; try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)); } catch { /* 화면은 동작 */ } return v; });
  /** 머리 칸 누르기 — 오름차순 → 내림차순 → 정렬 끔(등록 · 수정 최근 순) */
  const clickSort = (k: 'step' | 'org') => (lv.sort !== k ? putLv({ sort: k, dir: 1 }) : lv.dir === 1 ? putLv({ dir: -1 }) : putLv({ sort: '', dir: 1 }));
  const sortMark = (k: 'step' | 'org') => (lv.sort === k ? (lv.dir === 1 ? ' ▲' : ' ▼') : '');
  const groups = Array.from(new Set(projects.map(groupOf).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ko'));
  const toggleFold = (g: string) => setFold((f) => { const n = { ...f, [g]: !f[g] }; if (!n[g]) delete n[g]; try { localStorage.setItem(FOLD_KEY, JSON.stringify(n)); } catch { /* 저장 못 해도 화면은 동작 */ } return n; });
  const toggleSel = (ids: string[], on: boolean) => setSel((cur) => { const n = new Set(cur); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const run = async (ids: string[], g: string | null) => {
    if (!ids.length || busy) return false;
    setBusy(true);
    const done = await onGroup(ids, g);
    setBusy(false);
    return done;
  };
  const applyMove = async () => {
    const name = (moveTo === '+' ? newName : moveTo).replace(/\s+/g, ' ').trim();
    if (!name) return;
    if (await run(Array.from(sel), name)) { setSel(new Set()); setMoveTo(''); setNewName(''); if (fold[name]) toggleFold(name); }
  };
  const applyRename = async () => {
    if (!renaming) return;
    const to = renaming.to.replace(/\s+/g, ' ').trim();
    if (!to || to === renaming.from) { setRenaming(null); return; }
    const from = renaming.from;
    const ids = projects.filter((p) => groupOf(p) === from).map((p) => p.id);
    if (await run(ids, to)) {
      setRenaming(null);
      // 접기 상태는 새 이름으로 옮기고, 이미 있는 그룹과 합쳤으면 그 그룹을 펼쳐 옮긴 줄이 보이게
      setFold((f) => { const n = { ...f }; delete n[from]; delete n[to]; try { localStorage.setItem(FOLD_KEY, JSON.stringify(n)); } catch { /* 화면은 동작 */ } return n; });
    }
  };
  const mergeTarget = renaming && groups.includes(renaming.to.replace(/\s+/g, ' ').trim()) && renaming.to.replace(/\s+/g, ' ').trim() !== renaming.from ? renaming.to.replace(/\s+/g, ' ').trim() : '';
  useEffect(() => {   // 지운 프로젝트는 선택에서 뺀다
    setSel((cur) => { const live = new Set(projects.map((p) => p.id)); const n = new Set(Array.from(cur).filter((id) => live.has(id))); return n.size === cur.size ? cur : n; });
  }, [projects]);
  useEffect(() => {
    if (!ungroupArmed) return;
    const t = window.setTimeout(() => setUngroupArmed(null), 3500);
    return () => window.clearTimeout(t);
  }, [ungroupArmed]);
  const [q, setQ] = useState('');
  // 진행 주체 필터 — 로움 / 올댓벤처스 건이 한 목록에 섞이므로 사무소별로 본다 (지도사 2026-09-24)
  const [office, setOffice] = useState<OfficeFilter>(readFilter);
  // 이체 구분 · 사채 처리 거르기(2026-10-28 「LP 매칭할 때 참고」 · venture/terms)
  const [wd, pickWd] = useTermFilter(WITHDRAW_AXIS, 'venture.wdFilter');
  const [bond, pickBond] = useTermFilter(BOND_AXIS, 'venture.bondFilter');
  const pickOffice = (v: OfficeFilter) => { setOffice(v); try { localStorage.setItem(FILTER_KEY, v); } catch { /* 저장 못 해도 화면은 동작 */ } };
  const countOf = (v: OfficeFilter): number => (v === 'all' ? projects.length : projects.filter((p) => officeOf(p) === v).length);
  const view = projects.filter((p) => {
    if (office !== 'all' && officeOf(p) !== office) return false;
    if (!passes(WITHDRAW_AXIS, wd, p) || !passes(BOND_AXIS, bond, p)) return false;
    if (starOnly && !p.star) return false;
    const kw = q.trim().toLowerCase();
    if (!kw) return true;
    const oi = officeInfo(officeOf(p));
    const hay = [p.org, p.fp, p.fpCo, oi.short, oi.name, groupOf(p), ...(p.cos || []).map((c) => [c.name || '', c.fpName || '', c.fpCo || ''].join(' '))].join(' ').toLowerCase();
    return hay.includes(kw);
  });
  useEffect(() => {
    if (!armed) return;
    const t = window.setTimeout(() => setArmed(null), 3500);
    return () => window.clearTimeout(t);
  }, [armed]);

  /** 묶음 — 보기 기준이 「그룹」이면 그룹 이름 가나다순 · 미분류 맨 아래(그룹이 하나도 없으면 한 묶음 = 예전 목록) ·
   *  「진행단계」면 1단계 → 15단계 → 완료 · 「목록」이면 한 묶음. 묶음 안은 고른 정렬(없으면 최근 수정 순) */
  const showHead = lv.by === 'step' || (lv.by === 'group' && groups.length > 0);
  function sorted(rows: Project[]): Project[] {
    // 별표 조합은 묶음마다 맨 위(2026-10-19 「신경 써야 하는 조합」) — 그 안에서는 고른 정렬 · 없으면 최근 수정 순
    const starFirst = (a: Project, b: Project) => (b.star ? 1 : 0) - (a.star ? 1 : 0);
    if (!lv.sort) return rows.slice().sort(starFirst);
    const cmp = lv.sort === 'step' ? (a: Project, b: Project) => stepRank(a) - stepRank(b) || a.org.localeCompare(b.org, 'ko') : (a: Project, b: Project) => a.org.localeCompare(b.org, 'ko');
    return rows.slice().sort((a, b) => starFirst(a, b) || cmp(a, b) * lv.dir);
  }
  function sections(rows: Project[]): { key: string; label: string; sub: string; rows: Project[]; doneN: number; kind: 'group' | 'step' | 'none' }[] {
    if (!showHead) return [{ key: '', label: '', sub: '', rows: sorted(rows), doneN: 0, kind: 'none' }];
    const by = new Map<string, Project[]>();
    const keyOf = (p: Project) => (lv.by === 'step' ? 'S:' + (p.done ? 'done' : p.step) : groupOf(p) || NOGROUP);
    rows.forEach((p) => { const k = keyOf(p); const a = by.get(k); if (a) a.push(p); else by.set(k, [p]); });
    let keys: string[];
    if (lv.by === 'step') {
      keys = STEPS.map((_, i) => 'S:' + (i + 1)).concat(['S:done']);   // 건이 없는 단계도 머리줄만(0건) — 앞으로 갈 단계가 보이게 (2026-10-17 지도사 「향후 단계도 나오게 · 공란으로」)
      if (lv.sort === 'step' && lv.dir === -1) keys.reverse();
    } else keys = groups.filter((g) => by.has(g)).concat(by.has(NOGROUP) ? [NOGROUP] : []);
    return keys.map((k) => {
      const r = sorted(by.get(k) || []);
      if (lv.by === 'step') {
        const n = k.slice(2);
        return n === 'done'
          ? { key: k, label: '전체 완료', sub: '', rows: r, doneN: 0, kind: 'step' as const }
          : { key: k, label: n + '단계', sub: STEPS[Number(n) - 1] ? STEPS[Number(n) - 1][0] : '', rows: r, doneN: 0, kind: 'step' as const };
      }
      return { key: k, label: k === NOGROUP ? '미분류' : k, sub: '', rows: r, doneN: r.filter((p) => p.done).length, kind: 'group' as const };
    });
  }

  // 보이는 줄(검색 · 사무소 · 접기 반영) — 선택은 보이는 줄만 남긴다(숨은 줄에 묶기 · 빼기가 적용되지 않게)
  const secs = sections(view);
  const visIds = secs.reduce<string[]>((a, sec) => (sec.kind !== 'none' && fold[sec.key] ? a : a.concat(sec.rows.map((p) => p.id))), []);
  const visKey = visIds.join(',');
  useEffect(() => {
    const vis = new Set(visKey ? visKey.split(',') : []);
    setSel((cur) => { const n = new Set(Array.from(cur).filter((id) => vis.has(id))); return n.size === cur.size ? cur : n; });
  }, [visKey]);
  const ckRef = (some: boolean, all: boolean) => (el: HTMLInputElement | null) => { if (el) el.indeterminate = some && !all; };

  return (
    <>
      <div className="page-h">
        <div>
          <h1>{LOCKED_OFFICE ? '진행현황 (벤처투자유형)' : '벤처인증 프로젝트'}</h1>
          <p>벤처투자유형 · 개인투자조합 결성부터 벤처기업 확인서 발급까지</p>
        </div>
        <div className="ph-acts">
        {projects.length && !LOCKED_OFFICE ? (
          <div className="seg office-filt" role="radiogroup" aria-label="진행 주체별 보기">
            {([{ v: 'all' as OfficeFilter, short: '전체' }] as { v: OfficeFilter; short: string }[]).concat(OFFICES.map((o) => ({ v: o.v as OfficeFilter, short: o.short }))).map((o) => (
              <button key={o.v} type="button" className={office === o.v ? 'on' : ''} aria-pressed={office === o.v} title={o.v === 'all' ? '두 사무소 건 모두' : officeInfo(o.v as Office).name + ' 진행 건만'} onClick={() => pickOffice(o.v)}>
                {o.short} <span className="cnt">{countOf(o.v)}</span>
              </button>
            ))}
          </div>
        ) : null}
        <TermFilter axis={WITHDRAW_AXIS} items={projects} value={wd} onPick={pickWd} />
        <TermFilter axis={BOND_AXIS} items={projects} value={bond} onPick={pickBond} />
        {onLp ? <button className="btn" onClick={onLp} title="올댓벤처스 LP 후보">🤝 LP 후보{lpWait ? <span className="cnt">{lpWait}</span> : null}</button> : null}
        <button className="btn pri" onClick={onNew}>
          <Icon n="plus" /> 새 프로젝트 등록
        </button>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="empty">
          <div className="eic"><Icon n="folder" /></div>
          <b>아직 등록된 프로젝트가 없습니다</b>
          <p>첫 벤처인증 프로젝트를 등록해 {STEPS.length}단계 진행을 시작하세요.</p>
          <button className="btn pri" onClick={onNew} style={{ margin: '0 auto' }}>
            <Icon n="plus" /> 새 프로젝트 등록
          </button>
        </div>
      ) : (
        <div className="tablewrap">
          <div className="lv-bar">
            <IMEInput value={q} onChange={setQ} placeholder="🔍 조합 · 투자법인 · 소개자(FP) · 그룹 검색"
              style={{ width: '100%', maxWidth: 360, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--line-2)', fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--ink)', boxSizing: 'border-box' }} />
            {LOCKED_OFFICE ? null : <div className="seg lv-seg" role="radiogroup" aria-label="모아 보기 기준">
              {([['group', '그룹별'], ['step', '진행단계별'], ['none', '목록']] as [GroupBy, string][]).map(([v, l]) => (
                <button key={v} type="button" className={lv.by === v ? 'on' : ''} aria-pressed={lv.by === v} onClick={() => putLv({ by: v })}>{l}</button>
              ))}
            </div>}
            {onStar && (starN || starOnly) ? (
              <button type="button" className={'star-filt' + (starOnly ? ' on' : '')} aria-pressed={starOnly} onClick={() => setStarOnly((v) => !v)}
                title="별표(신경 써야 하는 조합)만 보기">★ 별표 {starN}</button>
            ) : null}
            {!sel.size && lv.by === 'group' && !groups.length ? <span className="grp-hint">왼쪽 칸을 체크해 그룹으로 묶을 수 있습니다</span> : null}
          </div>
          {sel.size ? (
            <div className="grp-bar" role="toolbar" aria-label="선택한 프로젝트 그룹 지정">
              <b>{sel.size}건 선택</b>
              <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} aria-label="옮길 그룹">
                <option value="">그룹 고르기…</option>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                <option value="+">＋ 새 그룹 만들기</option>
              </select>
              {moveTo === '+' ? (
                <IMEInput value={newName} onChange={setNewName} placeholder="새 그룹 이름" maxLength={40} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) applyMove(); }} />
              ) : null}
              <button className="btn sm pri" disabled={busy || !(moveTo === '+' ? newName.trim() : moveTo)} onClick={applyMove}>{busy ? '저장 중…' : '그룹으로 묶기'}</button>
              {Array.from(sel).some((id) => { const p = projects.find((x) => x.id === id); return !!(p && groupOf(p)); }) ? (
                <button className="btn sm" disabled={busy} onClick={async () => { if (await run(Array.from(sel), null)) setSel(new Set()); }}>그룹에서 빼기</button>
              ) : null}
              <button className="ghostdel grp-clear" onClick={() => { setSel(new Set()); setMoveTo(''); setNewName(''); }}>선택 해제</button>
            </div>
          ) : null}
          <table>
            <thead>
              <tr>
                <th className="ck">
                  <input type="checkbox" aria-label="보이는 프로젝트 모두 선택" checked={!!visIds.length && visIds.every((id) => sel.has(id))}
                    ref={ckRef(visIds.some((id) => sel.has(id)), !!visIds.length && visIds.every((id) => sel.has(id)))}
                    onChange={(e) => toggleSel(visIds, e.target.checked)} />
                </th>
                <th className="sortable" aria-sort={lv.sort === 'org' ? (lv.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => clickSort('org')} title="누를 때마다 가나다순 → 역순 → 최근 수정 순">조합 / 투자법인{sortMark('org')}</button>
                </th>
                <th className="sortable" aria-sort={lv.sort === 'step' ? (lv.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => clickSort('step')} title="누를 때마다 단계 낮은 순 → 높은 순 → 최근 수정 순">진행단계{sortMark('step')}</button>
                </th>
                <th>소개자(FP)</th>
                <th>상태</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', fontSize: 13 }}>{q.trim() ? '검색 결과가 없습니다' : officeInfo(office as Office).name + ' 진행 건이 아직 없습니다 — 등록할 때 「진행 주체」에서 고르거나, 등록정보에서 바꿀 수 있습니다'}</td></tr>
              ) : secs.map((sec) => {
                const ids = sec.rows.map((p) => p.id);
                const head = sec.kind === 'group' && sec.key !== NOGROUP ? sec.key : null;
                const isFold = !!fold[sec.key];
                const allOn = !isFold && ids.length > 0 && ids.every((id) => sel.has(id));
                const empty = ids.length === 0;   // 건이 없는 단계 — 머리줄만(흐리게 · 누르기 없음)
                const someOn = !isFold && ids.some((id) => sel.has(id));
                return [
                  sec.kind !== 'none' ? (
                    <tr key={'g:' + sec.key} className={'grp-h' + (sec.kind === 'step' ? ' by-step' : head ? '' : ' none') + (empty ? ' empty' : '')} onClick={empty ? undefined : () => toggleFold(sec.key)}>
                      <td className="ck" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={sec.label + ' 모두 선택'} checked={allOn} disabled={empty} ref={ckRef(someOn, allOn)}
                          onChange={(e) => { if (isFold) toggleFold(sec.key); toggleSel(ids, e.target.checked); }} />
                      </td>
                      <td colSpan={5}>
                        <div className="grp-row">
                          {empty ? <span className="grp-chev ph" aria-hidden="true" /> : <button type="button" className={'grp-chev' + (isFold ? '' : ' open')} aria-expanded={!isFold} aria-label={sec.label + (isFold ? ' 펼치기' : ' 접기')}
                            onClick={(e) => { e.stopPropagation(); toggleFold(sec.key); }}><Icon n="chev" /></button>}
                          {renaming && renaming.from === head ? (
                            <span className="grp-ren" onClick={(e) => e.stopPropagation()}>
                              <IMEInput value={renaming.to} onChange={(v) => setRenaming({ from: renaming.from, to: v })} maxLength={40} autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) applyRename(); if (e.key === 'Escape') setRenaming(null); }} />
                              <button className="btn sm pri" disabled={busy} onClick={applyRename}>{mergeTarget ? '「' + mergeTarget + '」과 합치기' : '저장'}</button>
                              <button className="btn sm" onClick={() => setRenaming(null)}>취소</button>
                            </span>
                          ) : <b className="grp-name">{sec.label}</b>}
                          {sec.sub ? <span className="grp-sub">{sec.sub}</span> : null}
                          <span className="grp-cnt">{sec.rows.length}건{sec.doneN ? ' · 완료 ' + sec.doneN : ''}</span>
                          {head && !(renaming && renaming.from === head) ? (
                            <span className="grp-act" onClick={(e) => e.stopPropagation()}>
                              <button className="grp-link" onClick={() => setRenaming({ from: head, to: head })}>이름 바꾸기</button>
                              {ungroupArmed === head ? (
                                <button className="pill p-amber" style={{ cursor: 'pointer', border: 'none' }} disabled={busy}
                                  onClick={async () => { const all = projects.filter((p) => groupOf(p) === head).map((p) => p.id); if (await run(all, null)) setUngroupArmed(null); }}>
                                  한 번 더 누르면 그룹 해제
                                </button>
                              ) : <button className="ghostdel" title="그룹만 없애고 프로젝트는 미분류로" onClick={() => setUngroupArmed(head)}>그룹 해제</button>}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null,
                ].concat(isFold && sec.kind !== 'none' ? [] : sec.rows.map((p) => {
                const pct = p.done ? 100 : Math.round((p.step / STEPS.length) * 100);
                const rep = repAlert(p);   // 정기·결산 보고 기간 도래(주황) / 지연(빨강) — 행 색칠 (2026-09-24)
                return (
                  <tr key={p.id} onClick={() => onOpen(p.id)} className={[rep ? 'rep-' + rep.stat : '', sel.has(p.id) ? 'is-sel' : '', sec.kind !== 'none' ? 'in-grp' : ''].filter(Boolean).join(' ') || undefined}>
                    <td className="ck" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={p.org + ' 선택'} checked={sel.has(p.id)} onChange={(e) => toggleSel([p.id], e.target.checked)} />
                    </td>
                    <td className="cell-name">
                      {onStar ? (
                        <button type="button" className={'star-btn' + (p.star ? ' on' : '')} aria-pressed={!!p.star} aria-label={p.star ? p.org + ' 별표 빼기' : p.org + ' 별표 — 신경 써야 하는 조합'}
                          title={p.star ? '별표 빼기' : '별표 — 신경 써야 하는 조합'} onClick={(e) => { e.stopPropagation(); onStar(p.id, !p.star); }}>{p.star ? '★' : '☆'}</button>
                      ) : null}
                      <b>{p.org}</b> {LOCKED_OFFICE ? null : <OfficePill p={p} />}<TermPills x={p} />
                      {lpByProject && lpByProject[p.id] && lpByProject[p.id].length ? (
                        <span className="pill lp-pill" title={'매칭된 LP 후보 · ' + lpByProject[p.id].join(', ')}>LP {lpByProject[p.id].length}</span>
                      ) : null}
                      <div className="sub">
                        {(p.cos || []).map((c) => (c.name || c.bizno || '(법인)') + (c.ceoRole === 'lp' ? '(LP)' : '')).join(', ')} · 출자 {p.total || '-'}원{p.ceoRole === 'lp' ? ' · 대표 LP 참여' : p.ceoRole === 'gp' ? ' · 대표 GP' : ''}
                      </div>
                      {projectPlan(p) ? <div className="sub inv" title={projectPlan(p)}>투자 처리 · {projectPlan(p)}</div> : null}   {/* 조합 단위 한 줄 「…」 · 전체는 마우스를 올리면(2026-10-16 · 2026-10-19 조합 단위) */}
                    </td>
                    <td>
                      <div className="mini-prog">
                        <span className="n mono">{p.done ? STEPS.length : p.step}/{STEPS.length}</span>
                        <div className="bar"><i style={{ width: pct + '%' }} /></div>
                      </div>
                      <div className="sub" style={{ marginTop: 5 }}>{p.done ? '✅ 전체 완료' : STEPS[p.step - 1][0]}</div>
                    </td>
                    <td>
                      {(() => {
                        // 소개자는 투자법인 단위 — 회사별 FP를 중복 없이. 같은 FP면 한 줄.
                        const fps = fpList(p);
                        if (!fps.length) return <span style={{ color: 'var(--muted)' }}>-</span>;
                        return fps.slice(0, 3).map((f, k) => (
                          <div key={k} style={{ whiteSpace: 'nowrap', marginTop: k ? 3 : 0 }}>
                            {f.name} <span className="pill p-gray" style={{ marginLeft: 4 }}>{f.co || '-'}</span>
                            {fps.length > 1 && f.companies.length ? <span className="sub" style={{ marginLeft: 4 }}>{f.companies.join(', ')}</span> : null}
                          </div>
                        )).concat(fps.length > 3 ? [<div key="more" className="sub">외 {fps.length - 3}명</div>] : []);
                      })()}
                    </td>
                    <td>
                      <span className={'pill ' + (p.done ? 'p-green' : 'p-amber')}>
                        {p.done ? '완료' : '진행중'}
                      </span>
                      {rep ? <div style={{ marginTop: 4 }}><span className={'pill ' + (rep.stat === 'late' ? 'p-red' : 'p-amber')} title={rep.list.map((x) => x.label).join(', ')}>📅 {rep.label}</span></div> : null}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}>
                        {armed === p.id ? (
                          <button className="pill p-amber" style={{ cursor: 'pointer', border: 'none' }} onClick={() => { onDelete(p.id); setArmed(null); }}>
                            한 번 더 누르면 삭제
                          </button>
                        ) : (
                          <button className="ghostdel" onClick={() => setArmed(p.id)}>삭제</button>
                        )}
                        <span style={{ cursor: 'pointer', color: 'var(--muted-2)', display: 'inline-flex' }} onClick={() => onOpen(p.id)}><Icon n="chev" style={{ width: 15, height: 15 }} /></span>
                      </div>
                    </td>
                  </tr>
                );
                }));
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
